import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ADMIN_ROLES } from "@/lib/auth/admin-roles";
import { formatCurrency } from "@/lib/formatting/currency";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { useAdminNav } from "@/shared/layout/nav";
import { Alert, Badge, Button, buttonClass, EmptyView, Icon, LoadingState, Tag, type Tone } from "@/shared/rd";
import { useLocale } from "@admin/app/locale";
import { elapsed, rankAttention, type AttentionItem, type AttentionTier } from "../lib/attention";
import { useAttentionSources, type AttentionSourceKey } from "../services/useAttentionSources";

type Translate = ReturnType<typeof useTranslations>;

const k = keys.dashboard;

const TIER: Record<AttentionTier, { tone: Tone; label: string }> = {
  now: { tone: "red", label: k.tierNow },
  today: { tone: "amber", label: k.tierToday },
  later: { tone: "grey", label: k.tierLater },
};

const SOURCE_LABEL: Record<AttentionSourceKey, string> = {
  failedIntegrations: k.sourceFailedIntegrations,
  overdueStores: k.sourceOverdueStores,
  walletTransfers: k.sourceWalletTransfers,
  areaSuggestions: k.sourceAreaSuggestions,
  paidServices: k.sourcePaidServices,
};

/** Doodle badge per kind of work: big things get badges. */
const BADGE: Record<AttentionItem["kind"], string> = {
  failedIntegrations: "hub",
  restrictedStores: "clock",
  overdueStores: "clock",
  walletTransfers: "currency",
  paidServiceCap: "chart",
  areaSuggestions: "map",
};

/** Re-render once a minute so "waiting 34 h" stays true between refetches. */
function useNow() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

function greetingKey(now: Date) {
  const hour = now.getHours();
  return hour < 12 ? k.greetMorning : hour < 18 ? k.greetAfternoon : k.greetEvening;
}

/**
 * The admin home: what needs an admin today, ranked by urgency, then every
 * section of the platform with a line on what it is for.
 */
export function DashboardPage() {
  const t = useTranslations();
  const locale = useLocale();
  const now = useNow();
  const { data: session } = useSessionQuery();
  const isSuperAdmin = session?.admin?.roles?.includes(ADMIN_ROLES.SUPER_ADMIN) ?? false;
  const { input, sources } = useAttentionSources(isSuperAdmin);
  const { groups } = useAdminNav({ locale, isSuperAdmin });

  const items = rankAttention(input, now);
  const pending = sources.filter((source) => source.isLoading);
  const failed = sources.filter((source) => source.isError);
  const settled = pending.length === 0;
  const base = `/${locale}/admin`;
  const firstName = session?.admin?.name?.trim().split(/\s+/)[0];

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 4, maxWidth: 720 }}>
        {firstName ? (
          <p style={{ margin: 0, fontSize: 26, fontWeight: 700, lineHeight: 1.25 }} dir="auto">
            {t(greetingKey(now), { name: firstName })}
          </p>
        ) : null}
        <p style={{ margin: 0, fontSize: 14, color: "var(--rd-text-secondary)" }}>
          {items.length > 0
            ? t(k.subtitleSome, { count: items.length })
            : settled && failed.length === 0
              ? t(k.subtitleNone)
              : " "}
        </p>
      </div>

      <section className="rd-card" style={{ overflow: "hidden" }} aria-labelledby="attention-title">
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 16px", borderBottom: "1px solid var(--rd-line-row)" }}>
          <h2 id="attention-title" style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
            {t(k.attentionTitle)}
          </h2>
          <span style={{ fontSize: 13, color: "var(--rd-text-tertiary)" }}>{t(k.attentionHint)}</span>
        </div>

        {failed.map((source) => (
          <div key={source.key} style={{ padding: "10px 16px 0" }}>
            <Alert
              action={
                <Button size="sm" onClick={source.refetch}>
                  {t(keys.shell.retry)}
                </Button>
              }
            >
              {t(k.sourceError, { source: t(SOURCE_LABEL[source.key]) })}
            </Alert>
          </div>
        ))}

        <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
          {items.map((item, index) => (
            <AttentionRow key={item.id} item={item} first={index === 0} now={now} locale={locale} base={base} t={t} />
          ))}
        </ul>

        {!settled && items.length === 0 ? (
          <div style={{ padding: 16 }}>
            <LoadingState
              title={t(keys.shell.loadingTitle, { page: t(k.attentionTitle).toLocaleLowerCase(locale) })}
              sub={t(k.checking, {
                sources: pending.map((source) => t(SOURCE_LABEL[source.key])).join(locale === "ar" ? "، " : ", "),
              })}
              rows={0}
            />
          </div>
        ) : !settled ? (
          <p style={{ margin: 0, padding: "12px 16px", fontSize: 13, color: "var(--rd-text-tertiary)" }} aria-live="polite">
            {t(k.checking, {
              sources: pending.map((source) => t(SOURCE_LABEL[source.key])).join(locale === "ar" ? "، " : ", "),
            })}
          </p>
        ) : items.length === 0 && failed.length === 0 ? (
          <EmptyView illustration="admin-all-clear" tone="green" title={t(k.subtitleNone)} sub={t(k.allClear)} />
        ) : null}
      </section>

      <h2 style={{ margin: "12px 0 0", fontSize: 15, fontWeight: 600 }}>{t(k.sectionsTitle)}</h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))", gap: "8px 36px" }}>
        {groups
          .filter((group) => group.key !== "overview")
          .map((group) => (
            <section key={group.key} className="rd-listsec" aria-labelledby={`sec-${group.key}`}>
              <h3 id={`sec-${group.key}`} className="rd-listsec-title" style={{ margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
                <Badge id={group.badge} size={28} />
                {group.header}
              </h3>
              {group.items.map((navItem) => (
                <Link key={navItem.key} to={navItem.href} className="rd-listsec-row" style={{ textDecoration: "none" }}>
                  <Icon icon={navItem.icon} size={18} color="var(--rd-text-icon)" />
                  <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
                    <span style={{ fontWeight: 500 }}>{navItem.label}</span>
                    <span style={{ fontSize: 12, color: "var(--rd-text-meta)" }}>{navItem.description}</span>
                  </span>
                  <Icon icon={ChevronRight} size={16} color="var(--rd-dot-note)" className="rd-flip-rtl" />
                </Link>
              ))}
            </section>
          ))}
      </div>
    </>
  );
}

function AttentionRow({
  item,
  first,
  now,
  locale,
  base,
  t,
}: {
  item: AttentionItem;
  first: boolean;
  now: Date;
  locale: string;
  base: string;
  t: Translate;
}) {
  const copy = describe(item, locale, base, t);
  const age = elapsed(item.since, now);
  const ageText = age
    ? t(age.unit === "minutes" ? k.ageMinutes : age.unit === "hours" ? k.ageHours : k.ageDays, { n: age.value })
    : null;
  const meta = [ageText && copy.ageKey ? t(copy.ageKey, { age: ageText }) : null, copy.target].filter(Boolean).join(" · ");
  const tier = TIER[item.tier];

  return (
    <li style={{ borderTop: first ? 0 : "1px solid var(--rd-line-row)" }}>
      <Link
        to={copy.href}
        className="rd-attn-row"
        style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", color: "var(--rd-text)", textDecoration: "none", flexWrap: "wrap" }}
      >
        <Badge id={BADGE[item.kind]} size={36} />
        <span style={{ flex: "1 1 320px", minWidth: 0, display: "flex", flexDirection: "column", gap: 2, lineHeight: 1.35 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 15, fontWeight: 600 }} dir="auto">
              {copy.title}
            </span>
            <Tag tone={tier.tone}>{t(tier.label)}</Tag>
          </span>
          <span style={{ fontSize: 13, color: "var(--rd-text-secondary)" }} dir="auto">
            {copy.detail}
          </span>
          {meta ? (
            <span style={{ fontSize: 12, color: item.late ? "var(--rd-red)" : "var(--rd-text-tertiary)", fontWeight: item.late ? 600 : 400 }}>
              {meta}
            </span>
          ) : null}
        </span>
        <span className={buttonClass(first ? "primary" : "secondary", "sm")} style={{ flex: "none" }}>
          {copy.action}
        </span>
      </Link>
    </li>
  );
}

function describe(item: AttentionItem, locale: string, base: string, t: Translate) {
  const f = item.facts;
  const text = (value: string | number | null) => (value === null ? "—" : String(value));

  switch (item.kind) {
    case "failedIntegrations":
      return {
        title: t(k.failedTitle, { count: item.count }),
        detail: t(f.storeName ? k.failedDetail : k.failedDetailNoStore, {
          provider: text(f.provider),
          store: text(f.storeName),
          error: f.error ? String(f.error) : "",
        }).trim(),
        ageKey: k.ageLastFailure,
        target: null,
        action: t(k.failedAction),
        href: `${base}/integration-logs?status=failed`,
      };
    case "restrictedStores":
    case "overdueStores":
      return {
        title: t(item.kind === "restrictedStores" ? k.restrictedTitle : k.overdueTitle, { count: item.count }),
        detail: t(item.kind === "restrictedStores" ? k.restrictedDetail : k.overdueDetail, {
          store: text(f.oldestStore),
          overdue: text(f.overdueTotal),
        }),
        ageKey: k.ageDue,
        target: null,
        action: t(k.storesAction),
        href: `${base}/stores/subscriptions?overdue=true`,
      };
    case "walletTransfers":
      return {
        title: t(k.walletTitle, { count: item.count }),
        detail:
          typeof f.totalAmount === "number"
            ? t(k.walletDetailAmount, {
                amount: formatCurrency(f.totalAmount, locale, f.currency ? String(f.currency) : undefined),
                store: text(f.oldestStore),
              })
            : t(k.walletDetail, { store: text(f.oldestStore) }),
        ageKey: k.ageWaiting,
        target: t(k.walletTarget),
        action: t(k.walletAction),
        href: `${base}/wallet-top-up-requests`,
      };
    case "paidServiceCap": {
      const service = text(locale === "ar" ? f.nameAr : f.nameEn);
      const reached = item.tier === "now";
      return {
        title: reached ? t(k.paidReachedTitle, { service }) : t(k.paidNearTitle, { service, percent: text(f.percent) }),
        detail: t(reached ? k.paidReachedDetail : k.paidNearDetail),
        ageKey: reached ? k.ageReached : null,
        target: null,
        action: t(k.paidAction),
        href: `${base}/paid-services`,
      };
    }
    case "areaSuggestions":
      return {
        title: t(k.areasTitle, { count: item.count }),
        detail: t(k.areasDetail, { name: text(f.mostAsked), times: text(f.mostAskedCount) }),
        ageKey: k.ageWaiting,
        target: null,
        action: t(k.areasAction),
        href: `${base}/area-suggestions`,
      };
  }
}
